import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { Buyer } from '@ps/core-models';
import { PlatformSwitcherRouteKey } from '@ps/platform-switcher-data-access';
import {
  AdvancedFeasibilityFilters,
  FindSurveysParams,
  LaunchToggleField,
  PatchableMetaData,
  RespondentCountsParams,
  RespondentFiltersRequest,
  SearchCriteriaSurveysParams,
  SearchGroupSurveysParams,
  SurveyLaunchFormState,
  SurveyLocaleData,
  SurveyMarginFormState,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
  TrackerConfig,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';
import { SurveyStatus } from '@ps/survey-kernel';

import {
  SurveyCreationPricingState,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsPreDraftActions,
  AdvancedFilterActions,
} from './insights';
import { OfferPriceApply } from './insights/utils/survey-creation-apply-offer-price.types';

import { SurveyChildKey } from './survey-creation-shared-state.model';
import {
  CHILD_SCOPE,
  SurveyChildScope,
} from './survey-creation-child-registry.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreChildrenActions,
} from './core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationMcActions,
  SurveyCreationPreDraftActions,
} from './audience/survey-creation-audience.actions';
import { SurveyCreationReferenceActions } from './reference/survey-creation-reference.actions';
import { SurveyCreationLaunchActions } from './launch/survey-creation-launch.actions';
import { SuppliersMetaData } from './audience/survey-creation-suppliers-meta-data-state.model';

@Injectable({ providedIn: 'root' })
export class SurveyCreationActionsFacade {
  private readonly _store = inject(Store);

  public initAudience(
    surveyId: Nullable<number>,
    recalculationsEnabled = true,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.init({ surveyId, recalculationsEnabled }),
    );
  }

  public initLaunch(
    surveyId: Nullable<number>,
    recalculationsEnabled = false,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.init({ surveyId, recalculationsEnabled }),
    );
  }

  public setActiveChildKey(activeChildKey: Nullable<SurveyChildKey>): void {
    this._store.dispatch(
      SurveyCreationCoreActions.setActiveChildKey({ activeChildKey }),
    );
  }

  public buildPreDraftChildren(localeData: SurveyLocaleData[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.buildPreDraftChildren({ localeData }),
    );
  }

  public addSurveyChild(locale: SurveyLocaleData): void {
    this._store.dispatch(SurveyCreationMcActions.addSurveyChild(locale));
  }

  public deleteSurveyChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationMcActions.deleteSurveyChild({ surveyId }),
    );
  }

  public createMcTraditionalSurveys(): void {
    this._store.dispatch(SurveyCreationMcActions.createMcTraditionalSurveys());
  }

  public createMcGroupedProject(): void {
    this._store.dispatch(SurveyCreationMcActions.createMcGroupedProject());
  }

  public selectAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectAllPreDraftChildren(),
    );
  }

  public clearAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.clearAllPreDraftChildren(),
    );
  }

  public selectPreDraftChildren(ids: SurveyChildKey[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectPreDraftChildren({ ids }),
    );
  }

  public selectChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.selectChild({ surveyId }),
    );
  }

  public updateChildSurveyCpi(surveyId: number, cpi: number): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.updateChildSurveyCpi({ surveyId, cpi }),
    );
  }

  public patchChildMetadata(
    changes: PatchableMetaData,
    scope: SurveyChildScope,
  ): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.patchChildMetadata({ changes, scope }),
    );
  }

  public switchChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationCoreChildrenActions.switchChild({ surveyId }),
    );
  }

  public createStandardSurvey(): void {
    this._store.dispatch(SurveyCreationAudienceActions.createStandardSurvey());
  }

  public changeCreatedSurveyLocale(
    locale: SurveyLocaleData,
    deleteCurrent: boolean,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.changeCreatedSurveyLocale({
        locale,
        deleteCurrent,
      }),
    );
  }

  public updateMetadata(
    metadata: SurveyMetadataFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateMetadata({ metadata, scope }),
    );
  }

  public patchChildrenMetadataByKey(
    changesByKey: ReadonlyMap<SurveyChildKey, PatchableMetaData>,
  ): void {
    changesByKey.forEach((changes, key) =>
      this.patchChildMetadata(changes, CHILD_SCOPE.keys([key])),
    );
  }

  public updateMargin(margin: SurveyMarginFormState): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateMargin({ margin }),
    );
  }

  public settleBuyerCpi(): void {
    this._store.dispatch(SurveyCreationAudienceActions.settleBuyerCpi());
  }

  public confirmHighCpi(cpi: number): void {
    this._store.dispatch(SurveyCreationAudienceActions.confirmHighCpi({ cpi }));
  }

  public selectTags(tagIds: number[]): void {
    this._store.dispatch(SurveyCreationAudienceActions.selectTags({ tagIds }));
  }

  public updateQuotas(
    quotas: SurveyQuotasFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateQuotas({ quotas, scope }),
    );
  }

  public updateQualifications(
    qualifications: SurveyQualificationsFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateQualifications({
        qualifications,
        scope,
      }),
    );
  }

  public updateLaunchForm(form: SurveyLaunchFormState): void {
    this._store.dispatch(SurveyCreationLaunchActions.updateForm({ form }));
  }

  public saveSurvey(recalculationsEnabled = true): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurvey({ recalculationsEnabled }),
    );
  }

  public createSurveyFromTemplate(
    templateId: number,
    deleteSurveyId: Nullable<number> = null,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.createSurveyFromTemplate({
        templateId,
        deleteSurveyId,
      }),
    );
  }

  public saveAndCloneSurvey(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndCloneSurvey());
  }

  public saveAndCreateNew(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndCreateNew());
  }

  public saveAndLaunch(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndLaunch());
  }

  public saveAndEditAudience(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndEditAudience());
  }

  public requestTestEntryLink(): void {
    this._store.dispatch(SurveyCreationLaunchActions.requestTestEntryLink());
  }

  public checkBillingCodeAlreadyUsed(billingId: string): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.checkBillingCodeAlreadyUsed({ billingId }),
    );
  }

  public loadAidaEligibility(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.loadAidaEligibility({ surveyId }),
    );
  }

  public loadLaunchPricing(): void {
    this._store.dispatch(SurveyCreationLaunchActions.loadLaunchPricing());
  }

  public launchSurvey(
    status: SurveyStatus,
    pricingConfirmationMessage: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.launchSurvey({
        status,
        pricingConfirmationMessage,
      }),
    );
  }

  // Dispatch for SurveyCreationWorkflowExitEffects — leaving the survey
  // workflow, with or without saving, with or without an impersonation change.
  public saveAndExit(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndExit());
  }

  public discardSurveyChanges(): void {
    this._store.dispatch(SurveyCreationCoreActions.discardSurveyChanges());
  }

  public saveSurveyAndDisconnectImpersonation(): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndDisconnectImpersonation(),
    );
  }

  public saveSurveyAndSwitchImpersonatedBuyer(buyer: Buyer): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndSwitchImpersonatedBuyer({ buyer }),
    );
  }

  public saveSurveyAndSwitchToLegacy(
    routeKey: PlatformSwitcherRouteKey,
    params: Record<string, string | number>,
  ): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy({
        routeKey,
        params,
      }),
    );
  }

  public reset(): void {
    this._store.dispatch(SurveyCreationCoreActions.reset());
  }

  // Dispatch for the Tracker Configuration Drawer's confirm.
  public setupTrackerConfig(config: TrackerConfig): void {
    this._store.dispatch(
      SurveyCreationCoreActions.setupTrackerConfig({ config }),
    );
  }

  // Dispatch for the Tracker banner's "Reset to Standard Survey" link.
  public resetTrackerConfig(): void {
    this._store.dispatch(SurveyCreationCoreActions.resetTrackerConfig());
  }

  public uploadLocationCodes(
    countryCode: string,
    qualificationCode: number,
    qualificationName: string,
    file: File,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.uploadLocationCodes({
        countryCode,
        qualificationCode,
        qualificationName,
        file,
      }),
    );
  }

  public clearLocationCodes(
    qualificationCode: number,
    qualificationName: string,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.clearLocationCodes({
        qualificationCode,
        qualificationName,
      }),
    );
  }

  public downloadLocationCodesTemplate(
    countryCode: string,
    qualificationCode: number,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.downloadLocationCodesTemplate({
        countryCode,
        qualificationCode,
      }),
    );
  }

  public downloadValidatedLocationFile(qualificationCode: number): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.downloadValidatedLocationFile({
        qualificationCode,
      }),
    );
  }

  public downloadEntryLinksTemplate(): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.downloadEntryLinksTemplate(),
    );
  }

  public updateBasicToken(enable: boolean, value: string): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateBasicToken({ enable, value }),
    );
  }

  public downloadRespondentListTemplate(): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.downloadRespondentListTemplate(),
    );
  }

  public uploadRespondentList(file: File): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.uploadRespondentList({ file }),
    );
  }

  public toggleIncludeExcludeStatus(enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.toggleIncludeExcludeStatus({ enabled }),
    );
  }

  public resolveRespondentFilters(request: RespondentFiltersRequest): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.resolveRespondentFilters(request),
    );
  }

  public updateRespondentStatuses(statuses: number[]): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateRespondentStatuses({ statuses }),
    );
  }

  public clearRespondentList(): void {
    this._store.dispatch(SurveyCreationLaunchActions.clearRespondentList());
  }

  public logLaunchFieldToggled(
    surveyId: number,
    field: LaunchToggleField,
    enabled: boolean,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'launchToggle', field, enabled },
      }),
    );
  }

  public logAidaToggled(surveyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'aidaToggled', enabled },
      }),
    );
  }

  public logUniqueBuyerEntryLinksActivity(
    surveyId: number,
    status: 'uploaded' | 'cleared',
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger:
          status === 'uploaded'
            ? { kind: 'uniqueBuyerEntryLinksUploaded' }
            : { kind: 'uniqueBuyerEntryLinksCleared' },
      }),
    );
  }

  public logEmailNotificationChanged(
    surveyId: number,
    from: string,
    to: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'emailNotificationChanged', from, to },
      }),
    );
  }

  public logExternalSurveyIdChanged(
    surveyId: number,
    externalSurveyId: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'externalSurveyIdChanged', externalSurveyId },
      }),
    );
  }

  public logSelectedProjectManagerChanged(
    surveyId: number,
    from: string,
    to: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'selectedProjectManager', from, to },
      }),
    );
  }

  public loadQualifications(localeData: SurveyLocaleData[]): void {
    for (const eachLocaleData of localeData) {
      this._store.dispatch(
        SurveyCreationReferenceActions.loadQualifications(eachLocaleData),
      );
    }
  }

  public loadQuestionCategories(): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestionCategories(),
    );
  }

  public loadQuestions(
    search: string,
    categoryIds: number[],
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestions({
        search,
        categoryIds,
        locale,
      }),
    );
  }

  public loadDependencyParentQuestion(
    parentQualificationId: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadDependencyParentQuestion({
        parentQualificationId,
        locale,
      }),
    );
  }

  public loadLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadLocationConditions({
        dbQualificationCode,
        locale,
      }),
    );
  }

  public toggleManualProcessing(companyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.toggleManualProcessing({
        companyId,
        enabled,
      }),
    );
  }

  public applyCpiPricing(
    pricing: SurveyCreationPricingState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.applyCpiPricing({ ...pricing, scope }),
    );
  }

  public applyOfferPrice(apply: OfferPriceApply): void {
    this._store.dispatch(SurveyCreationInsightsActions.applyOfferPrice(apply));
  }

  public setSuppliersMetaData(metaData: Partial<SuppliersMetaData>): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.setSuppliersMetaData({ metaData }),
    );
  }

  public findSurveys(params: FindSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.findSurveys({ params }));
  }

  public getRespondentCounts(params: RespondentCountsParams): void {
    this._store.dispatch(AdvancedFilterActions.getRespondentCounts({ params }));
  }

  public searchCriteriaSurveys(params: SearchCriteriaSurveysParams): void {
    this._store.dispatch(
      AdvancedFilterActions.searchCriteriaSurveys({ params }),
    );
  }

  public searchGroupSurveys(params: SearchGroupSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.searchGroupSurveys({ params }));
  }

  public clearSurveyOptions(): void {
    this._store.dispatch(AdvancedFilterActions.clearSurveyOptions());
  }

  public applyAdvancedFilters(
    filters: AdvancedFeasibilityFilters,
    clearUploadedPsidsFor: Nullable<number>,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.applyAdvancedFilters({
        filters,
        clearUploadedPsidsFor,
      }),
    );
  }

  public resetAdvancedFilters(): void {
    this._store.dispatch(AdvancedFilterActions.resetFilters());
  }

  public resyncSimilarSurveys(): void {
    this._store.dispatch(SurveyCreationInsightsActions.resyncSimilarSurveys());
  }

  public searchSimilarSurveysOutsideAccount(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.searchSimilarSurveysOutsideAccount(),
    );
  }

  public cancelSimilarSurveysSearch(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.cancelSimilarSurveysSearch(),
    );
  }

  public calculateFeasibilityForPreDraft(): void {
    this._store.dispatch(
      SurveyCreationInsightsPreDraftActions.calculateFeasibilityForPreDraft(),
    );
  }
}
